/**
 * DrainageDataGrid Component
 * Inline editing table for drainage products
 */

import React, { useCallback, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    IconButton,
    Tooltip,
    Chip,
    styled,
    TableSortLabel,
    TablePagination,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    OutlinedInput,
    SelectChangeEvent,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import { IAdminDrainageProduct, IAdminDrainageProductRequest, APPLICABLE_LAYERS } from '../types/AdminTypes';

// Styled components
const StyledTableContainer = styled(TableContainer)(() => ({
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: 'calc(100vh - 350px)',
    width: '100%',
}));

const StyledTableHead = styled(TableHead)(() => ({
    '& .MuiTableCell-head': {
        backgroundColor: '#004652', // Maccaferri brand green - must be on cell for stickyHeader to work
        fontWeight: 'bold',
        fontSize: '0.8rem',
        padding: '10px 6px',
        borderRight: '1px solid rgba(255,255,255,0.2)',
        color: 'white',
        '&:last-child': {
            borderRight: 'none',
        },
        '& .MuiTableSortLabel-root': {
            color: 'white',
            '&:hover': {
                color: 'rgba(255,255,255,0.8)',
            },
            '&.Mui-active': {
                color: 'white',
                '& .MuiTableSortLabel-icon': {
                    color: 'white',
                },
            },
        },
    },
}));

const StyledTableRow = styled(TableRow)<{ editing?: number }>(({ editing }) => ({
    backgroundColor: editing ? '#e8f5e9' : 'inherit',
    '&:hover': {
        backgroundColor: editing ? '#e8f5e9' : '#f5f5f5',
    },
    '& .MuiTableCell-body': {
        padding: '6px',
        borderRight: '1px solid #e0e0e0',
        fontSize: '0.8rem',
        '&:last-child': {
            borderRight: 'none',
        },
    },
}));

const EditableCell = styled(TableCell)(() => ({
    '& .MuiTextField-root': {
        '& input': {
            padding: '4px 6px',
            fontSize: '0.8rem',
        },
    },
}));

interface DrainageDataGridProps {
    products: IAdminDrainageProduct[];
    onUpdate: (id: string, product: IAdminDrainageProductRequest) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
    loading?: boolean;
}

type Order = 'asc' | 'desc';

export const DrainageDataGrid: React.FC<DrainageDataGridProps> = ({ products, onUpdate, onDelete, loading }) => {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editedProduct, setEditedProduct] = useState<Partial<IAdminDrainageProduct>>({});
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<keyof IAdminDrainageProduct>('displayName');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const handleRequestSort = (property: keyof IAdminDrainageProduct) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleStartEdit = useCallback((product: IAdminDrainageProduct) => {
        setEditingId(product.id);
        setEditedProduct({ ...product });
    }, []);

    const handleCancelEdit = useCallback(() => {
        setEditingId(null);
        setEditedProduct({});
    }, []);

    const handleSaveEdit = useCallback(async () => {
        if (!editingId || !editedProduct) return;

        const request: IAdminDrainageProductRequest = {
            displayName: editedProduct.displayName || '',
            category: editedProduct.category || '',
            effectivePorosity: editedProduct.effectivePorosity,
            permeabilityCoefficient: editedProduct.permeabilityCoefficient,
            transmissivity: editedProduct.transmissivity,
            thickness: editedProduct.thickness,
            rfCreep: editedProduct.rfCreep,
            description: editedProduct.description,
            databaseReference: editedProduct.databaseReference,
            notes: editedProduct.notes,
            applicableLayersOnly: editedProduct.applicableLayersOnly,
        };

        try {
            await onUpdate(editingId, request);
            setEditingId(null);
            setEditedProduct({});
        } catch (error) {
            console.error('Error saving product:', error);
        }
    }, [editingId, editedProduct, onUpdate]);

    const handleFieldChange = useCallback((field: keyof IAdminDrainageProduct, value: string | number | null) => {
        setEditedProduct(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleDelete = useCallback(
        async (id: string) => {
            if (window.confirm('Are you sure you want to delete this product?')) {
                await onDelete(id);
            }
        },
        [onDelete]
    );

    // Sorting
    const sortedProducts = [...products].sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return order === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return 0;
    });

    // Pagination
    const paginatedProducts = sortedProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const renderCell = (product: IAdminDrainageProduct, field: keyof IAdminDrainageProduct, type: 'text' | 'number') => {
        const isEditing = editingId === product.id;
        const value = isEditing ? editedProduct[field] : product[field];

        if (isEditing) {
            return (
                <TextField
                    size="small"
                    type={type}
                    value={value ?? ''}
                    onChange={e => {
                        const newValue = type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value;
                        handleFieldChange(field, newValue);
                    }}
                    variant="outlined"
                    fullWidth
                    inputProps={type === 'number' ? { step: 0.001, min: 0 } : {}}
                />
            );
        }

        if (type === 'number' && value !== null && value !== undefined) {
            return typeof value === 'number' ? value.toFixed(4) : value;
        }

        return value ?? '-';
    };

    const renderLayerChipsView = (layers: string[] | null | undefined) => {
        if (!layers || layers.length === 0) {
            return <span style={{ color: '#999' }}>None</span>;
        }
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {layers.map(layer => (
                    <Chip key={layer} label={layer.substring(0, 3).toUpperCase()} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                ))}
            </Box>
        );
    };

    const renderLayersEditor = (selectedLayers: string[]) => {
        const handleLayersChange = (event: SelectChangeEvent<string[]>) => {
            const { value } = event.target;
            const nextLayers = typeof value === 'string' ? value.split(',') : value;
            setEditedProduct(prev => ({ ...prev, applicableLayersOnly: nextLayers }));
        };
        return (
            <FormControl fullWidth size="small">
                <InputLabel id="layers-editor-label">Layers</InputLabel>
                <Select
                    labelId="layers-editor-label"
                    id="layers-editor-select"
                    multiple
                    value={selectedLayers}
                    onChange={handleLayersChange}
                    input={<OutlinedInput label="Layers" />}
                    renderValue={selected => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map(value => (
                                <Chip key={value} label={value.substring(0, 3).toUpperCase()} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                            ))}
                        </Box>
                    )}>
                    {APPLICABLE_LAYERS.map(layer => (
                        <MenuItem key={layer} value={layer}>
                            {layer.toUpperCase()}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    return (
        <Box>
            <Paper
                sx={{
                    overflow: 'hidden',
                    // Override global compact-input rule (style/mui.scss) inside edit rows
                    // so multi-select labels (Layers) can center correctly.
                    '& .MuiOutlinedInput-root': { height: 'auto !important' },
                }}>
                <StyledTableContainer>
                    <Table stickyHeader size="small">
                        <StyledTableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 60, width: 60 }}>Actions</TableCell>
                                <TableCell sx={{ minWidth: 100 }}>
                                    <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleRequestSort('id')}>
                                        ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 150 }}>
                                    <TableSortLabel active={orderBy === 'displayName'} direction={orderBy === 'displayName' ? order : 'asc'} onClick={() => handleRequestSort('displayName')}>
                                        Display Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 100 }}>
                                    <TableSortLabel active={orderBy === 'category'} direction={orderBy === 'category' ? order : 'asc'} onClick={() => handleRequestSort('category')}>
                                        Category
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 80 }}>
                                    <TableSortLabel
                                        active={orderBy === 'effectivePorosity'}
                                        direction={orderBy === 'effectivePorosity' ? order : 'asc'}
                                        onClick={() => handleRequestSort('effectivePorosity')}>
                                        Porosity
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 90 }}>
                                    <TableSortLabel
                                        active={orderBy === 'permeabilityCoefficient'}
                                        direction={orderBy === 'permeabilityCoefficient' ? order : 'asc'}
                                        onClick={() => handleRequestSort('permeabilityCoefficient')}>
                                        Permeability
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 90 }}>
                                    <TableSortLabel active={orderBy === 'transmissivity'} direction={orderBy === 'transmissivity' ? order : 'asc'} onClick={() => handleRequestSort('transmissivity')}>
                                        Transmissivity
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 70 }}>
                                    <TableSortLabel active={orderBy === 'thickness'} direction={orderBy === 'thickness' ? order : 'asc'} onClick={() => handleRequestSort('thickness')}>
                                        Thickness
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 70 }}>
                                    <TableSortLabel active={orderBy === 'rfCreep'} direction={orderBy === 'rfCreep' ? order : 'asc'} onClick={() => handleRequestSort('rfCreep')}>
                                        RF Creep
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
                                <TableCell sx={{ minWidth: 110 }}>Layers</TableCell>
                            </TableRow>
                        </StyledTableHead>
                        <TableBody>
                            {paginatedProducts.map(product => (
                                <StyledTableRow key={product.id} editing={editingId === product.id ? 1 : 0}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {editingId === product.id ? (
                                                <>
                                                    <Tooltip title="Save">
                                                        <IconButton size="small" onClick={handleSaveEdit} color="primary" disabled={loading}>
                                                            <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <IconButton size="small" onClick={handleCancelEdit} color="default">
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleStartEdit(product)} disabled={loading || editingId !== null}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleDelete(product.id)} color="error" disabled={loading || editingId !== null}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', color: '#666' }}>{product.id}</TableCell>
                                    <EditableCell>{renderCell(product, 'displayName', 'text')}</EditableCell>
                                    <TableCell>{product.category}</TableCell>
                                    <EditableCell>{renderCell(product, 'effectivePorosity', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'permeabilityCoefficient', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'transmissivity', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'thickness', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'rfCreep', 'number')}</EditableCell>
                                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        <Tooltip title={product.description || ''}>
                                            <span>{product.description || '-'}</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        {editingId === product.id ? renderLayersEditor(editedProduct.applicableLayersOnly ?? []) : renderLayerChipsView(product.applicableLayersOnly)}
                                    </TableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={products.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={e => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
        </Box>
    );
};
